"use client";
import { useNavigate } from "react-router-dom";
import PageContainer from "@/app/admin/_common/PageContainer";
import PageHeader from "@/app/admin/_common/PageHeader";
import CuoButton from "@/app/admin/_common/CuoButton";
import { Building2, ArrowLeft, Pencil } from "lucide-react";
import { useLocationDetailModel } from "./locationDetailPageModel";
import LocationDetailKpiRow from "./_cmp/LocationDetailKpiRow";
import LocationSalesCard from "./_cmp/LocationSalesCard";
import LocationPerformersList from "./_cmp/LocationPerformersList";
import LocationShowsList from "./_cmp/LocationShowsList";
import LocationDetailSkeleton from "./_cmp/LocationDetailSkeleton";
import { LocationDialog } from "../_cmp/locationForm/LocationDialog";

export default function LocationDetailPage() {
  const navigate = useNavigate();
  const { data, isLoading, openEditForm, openEdit, closeEdit, refetch } = useLocationDetailModel();

  const location = data?.location;
  const address = location
    ? [
        location.address_line_1,
        location.address_line_2,
        location.city,
        location.state,
        location.zip,
      ]
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    <PageContainer flush>
      <div className="flex h-full w-full flex-col">
        <PageHeader
          title={
            <div className="flex min-w-0 items-center gap-2">
              <CuoButton
                variant="ghost"
                size="sm"
                className="h-7 w-7 flex-none px-0"
                onClick={() => navigate("/admin/locations")}
              >
                <ArrowLeft className="size-4" />
              </CuoButton>
              <Building2 className="size-5 flex-none text-foreground" />
              <div className="flex min-w-0 flex-col justify-center leading-tight">
                <span className="truncate">{location?.name ?? "Location"}</span>
                {address && (
                  <span className="truncate text-[12px] font-normal text-muted-foreground">
                    {address}
                  </span>
                )}
              </div>
            </div>
          }
          actions={
            <CuoButton
              variant="outline"
              label={<span className="hidden sm:inline">Edit location</span>}
              icon={<Pencil className="size-3.5 shrink-0" size={14} />}
              onClick={openEdit}
              disabled={!location}
              className="px-2 sm:px-3"
              aria-label="Edit location"
            />
          }
        />

        <div className="flex min-h-0 flex-1 flex-col gap-3 px-3 py-5 md:px-5">
          {isLoading || !data ? (
            <LocationDetailSkeleton />
          ) : (
            <>
              <LocationDetailKpiRow kpis={data.kpis} />

              <div className="flex min-h-0 flex-1 flex-col gap-3 md:flex-row">
                <div className="-mx-3 min-h-0 min-w-0 md:mx-0 md:basis-[60%]">
                  <LocationShowsList shows={data.shows} />
                </div>
                <div className="flex min-w-0 flex-col gap-3 md:basis-[40%]">
                  <LocationSalesCard sales={data.sales} />
                  <LocationPerformersList performers={data.performers} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <LocationDialog
        open={openEditForm}
        defaultLocation={location}
        onClose={closeEdit}
        onSaved={refetch}
      />
    </PageContainer>
  );
}
